/*
 * EB Gift Reconciler — añade/quita regalos según subtotal real (excluyendo _es_regalo).
 * - Respeta opt-out del usuario: si quita un regalo manualmente, no se re-añade hasta que
 *   (a) cruza el threshold hacia abajo, (b) lo vuelve a añadir desde eb-gift-tray, o
 *   (c) se recarga la sesión.
 * - Lock + queue para evitar reentradas y race conditions con el cart drawer morph.
 * - Tag 'eb-gift-reconciler' para evitar loops.
 */
(function () {
  if (window.EBGiftReconciler) return;

  const OPTOUT_KEY = 'eb_gift_optouts_v1';

  let CONFIG = null;
  let lock = false;
  let queued = false;
  let scheduleHandle = null;
  let prevGiftKeys = {};
  let previousRealSubtotal = null;

  const optedOut = loadOptOuts();

  function loadOptOuts() {
    try {
      const raw = sessionStorage.getItem(OPTOUT_KEY);
      return new Set(raw ? JSON.parse(raw) : []);
    } catch (_e) { return new Set(); }
  }

  function saveOptOuts() {
    try { sessionStorage.setItem(OPTOUT_KEY, JSON.stringify(Array.from(optedOut))); }
    catch (_e) { /* ignore */ }
  }

  function isOptedOut(tier) { return optedOut.has(tier); }

  function setOptedOut(tier, value) {
    if (value) optedOut.add(tier);
    else optedOut.delete(tier);
    saveOptOuts();
    dispatchTrayUpdate();
  }

  // Debounce con rAF para evitar parpadeo del tray cuando hay múltiples
  // notificaciones en el mismo frame (cart:update + push + subscribe).
  let trayDispatchScheduled = false;
  function dispatchTrayUpdate() {
    if (trayDispatchScheduled) return;
    trayDispatchScheduled = true;
    requestAnimationFrame(() => {
      trayDispatchScheduled = false;
      document.dispatchEvent(new CustomEvent('eb-gift:state-change'));
    });
  }

  function extractGiftKeys(items) {
    const keys = {};
    for (const it of items || []) {
      const tier = it.properties && it.properties._es_regalo;
      if (tier && !keys[tier]) keys[tier] = it.key;
    }
    return keys;
  }

  function trackUserRemoval(state, source) {
    const currGiftKeys = extractGiftKeys(state.items);
    const items = state.items || [];
    const isCartEmpty = items.length === 0;
    // Detectar "cart reset/clear": antes había gifts + items principales, ahora
    // no hay nada. Eso NO es un opt-out (cliente quitando un regalo); es un
    // wipe del carrito. No marcamos opt-out en ese caso.
    const hadAnyGift = !!(prevGiftKeys.t1 || prevGiftKeys.t2 || prevGiftKeys.t3);

    if (source !== 'eb-gift-reconciler' && !isCartEmpty) {
      // Tracking solo cuando hay productos en el carrito. Si el cart está vacío,
      // no hay forma de distinguir "user opted out de un regalo" vs "user limpió cart".
      for (const tier of ['t1','t2','t3']) {
        if (prevGiftKeys[tier] && !currGiftKeys[tier]) {
          optedOut.add(tier);
          saveOptOuts();
        }
      }
    } else if (isCartEmpty && hadAnyGift) {
      // Cart vacío → asumimos reset. Limpiamos opt-outs para que la próxima
      // compra empiece limpia. (Si el cliente realmente NO quería un regalo,
      // puede volver a quitarlo desde el tray.)
      if (optedOut.size > 0) {
        optedOut.clear();
        saveOptOuts();
      }
    }

    prevGiftKeys = currGiftKeys;
  }

  function maybeResetOptOuts(realSubtotal) {
    // Reset opt-out for tiers the user has dropped below — next time they re-add
    // items crossing the threshold, the gift re-appears fresh.
    if (!CONFIG || !CONFIG.tiers) return;
    let changed = false;
    for (const tier of CONFIG.tiers) {
      if (isOptedOut(tier.id) && realSubtotal < tier.threshold) {
        optedOut.delete(tier.id);
        changed = true;
      }
    }
    if (changed) saveOptOuts();
  }

  function realSubtotalEur(items) {
    return (items || [])
      .filter((it) => !(it.properties && it.properties._es_regalo))
      .reduce((sum, it) => sum + (it.original_line_price ?? it.line_price ?? 0), 0)
      / 100;
  }

  function hasActivePack(items) {
    // Cuenta cuántos items distintos comparten _pack_grupo. Si >=4 (PACK_MIN_ITEMS),
    // hay un pack activo — y los gifts no se añaden porque la Discount Function de
    // Shopify NO permite emitir 2 productDiscountsAdd operations simultáneas
    // (bug observado: con pack+gifts ningún discount aplica). El cliente con pack
    // recibe -10% sobre todo el pack, que típicamente vale más que los gifts.
    const groupCounts = {};
    for (const it of items || []) {
      const grp = it.properties && it.properties._pack_grupo;
      if (!grp) continue;
      groupCounts[grp] = (groupCounts[grp] || 0) + 1;
    }
    for (const k in groupCounts) {
      if (groupCounts[k] >= 4) return true;
    }
    return false;
  }

  function buildDesired(subtotal, items) {
    const desired = {};
    // Si hay un pack activo, no añadimos ningún regalo: gifts y pack-discount son
    // mutuamente excluyentes por la limitación del Shopify Discount API.
    if (hasActivePack(items)) return desired;
    for (const tier of CONFIG.tiers) {
      if (!tier.variantId) continue;
      if (isOptedOut(tier.id)) continue;
      if (subtotal < tier.threshold) continue;
      // Si el cliente ya tiene esa variante en el carrito (manual, sin _es_regalo o con tier distinto),
      // no duplicamos la línea — respetamos su compra manual. Solo mantenemos la línea gift si ya es
      // nuestro regalo correcto.
      const existing = (items || []).find((it) => String(it.variant_id) === String(tier.variantId));
      if (existing) {
        const isOurGift = existing.properties && existing.properties._es_regalo === tier.id;
        if (!isOurGift) continue;
      }
      desired[tier.id] = String(tier.variantId);
    }
    return desired;
  }

  function indexCurrent(items) {
    const first = {};
    const dupes = [];
    for (const it of items || []) {
      const tier = it.properties && it.properties._es_regalo;
      if (!tier) continue;
      if (!first[tier]) first[tier] = it;
      else dupes.push(it.key);
    }
    return { first, dupes };
  }

  function init(config) {
    CONFIG = config;
    if (!CONFIG || !CONFIG.tiers || CONFIG.tiers.length === 0) return;

    const start = () => {
      if (!window.EBCart) return setTimeout(start, 60);
      window.EBCart.subscribe(schedule);
    };
    start();
  }

  function schedule(state, source) {
    if (!state || !state.hydrated) return;

    // 1) Detect user-removal → mark opt-out (skip for our own internal sources)
    const isOwnSource = typeof source === 'string' && source.indexOf('eb-gift-reconciler') === 0;
    if (!isOwnSource) {
      trackUserRemoval(state, source);
    }

    // 2) Reset opt-out for tiers we've dropped below
    const realSubtotal = realSubtotalEur(state.items);
    maybeResetOptOuts(realSubtotal);
    previousRealSubtotal = realSubtotal;

    // 3) Ignore our own sources (eb-gift-reconciler, eb-gift-reconciler-prefetch, etc.)
    //    Sin esto, el push interno tras el prefetch dispara otro schedule → reconcile
    //    → push → loop. El tray parpadea constantemente.
    if (isOwnSource) {
      dispatchTrayUpdate();
      return;
    }

    // 4) Schedule reconcile (debounced 320ms)
    if (scheduleHandle) clearTimeout(scheduleHandle);
    scheduleHandle = setTimeout(() => reconcile(state), 320);
    dispatchTrayUpdate();
  }

  async function reconcile(state) {
    if (lock) { queued = true; return; }
    lock = true;
    try {
      // SIEMPRE refrescar el state desde el servidor antes de reconciliar.
      // Pitch a veces dispara cart:update con resource stale (sin items refrescados),
      // lo que hacía que reconcile leyera items obsoletos y no quitara los regalos
      // cuando el usuario eliminaba el producto principal. Bug crítico (regalos a 0€
      // checkoutables) reportado 2026-04-25.
      let items;
      try {
        const fresh = await fetch('/cart.js', { credentials: 'same-origin', headers: { 'Accept': 'application/json' } });
        if (fresh.ok) {
          const data = await fresh.json();
          items = data.items || [];
          // Sincronizar EBCart con el dato fresco para que progress bar/tray vean lo mismo
          if (window.EBCart && window.EBCart.push) {
            window.EBCart.push({
              item_count: data.item_count,
              items,
              items_subtotal_price: data.items_subtotal_price ?? data.total_price ?? 0,
              total_price: data.total_price,
              currency: data.currency
            }, 'eb-gift-reconciler-prefetch');
          }
        } else {
          items = (state && state.items) || [];
        }
      } catch (_e) {
        items = (state && state.items) || [];
      }
      const subtotal = realSubtotalEur(items);
      const desired = buildDesired(subtotal, items);
      const { first: currentByTier, dupes } = indexCurrent(items);

      const toRemoveKeys = [...dupes];
      const toAdd = [];
      const toCapQty = []; // { key } — líneas de regalo con quantity > 1 que hay que bajar a 1

      // --- LIMPIEZA DE TIERS DESACTIVADOS (campañas estacionales) ---
      // Si un tier se apaga en el editor deja de estar en CONFIG.tiers. Quitamos
      // las líneas de regalo de ESE tier que quedaran en carritos antiguos (caso
      // pedido #6171: peine con T2 ya apagado). Solo toca líneas con _es_regalo
      // de un tier que YA NO está activo — nunca productos normales, packs ni
      // tiers activos. Si todos los tiers están activos, este bucle no hace nada.
      const activeTierIds = new Set(CONFIG.tiers.map((t) => t.id));
      for (const it of (items || [])) {
        const existingTier = it.properties && it.properties._es_regalo;
        if (existingTier && !activeTierIds.has(existingTier)) toRemoveKeys.push(it.key);
      }

      for (const tier of CONFIG.tiers) {
        const want = desired[tier.id];
        const have = currentByTier[tier.id];

        if (isOptedOut(tier.id)) {
          if (have) toRemoveKeys.push(have.key);
          continue;
        }

        if (want) {
          if (!have) {
            toAdd.push({ tier: tier.id, variantId: want });
          } else if (String(have.variant_id) !== String(want)) {
            toRemoveKeys.push(have.key);
            toAdd.push({ tier: tier.id, variantId: want });
          } else if ((have.quantity || 1) > 1) {
            // Un regalo debe ser siempre 1 unidad; corregir si cliente subió cantidad
            toCapQty.push(have.key);
          }
        } else if (have) {
          toRemoveKeys.push(have.key);
        }
      }

      if (toRemoveKeys.length === 0 && toAdd.length === 0 && toCapQty.length === 0) {
        dispatchTrayUpdate();
        return;
      }

      for (const key of [...new Set(toRemoveKeys)]) {
        try {
          await fetch('/cart/change.js', {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ id: key, quantity: 0 }),
          });
        } catch (_e) { /* swallow */ }
      }

      for (const key of toCapQty) {
        try {
          await fetch('/cart/change.js', {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ id: key, quantity: 1 }),
          });
        } catch (_e) { /* swallow */ }
      }

      if (toAdd.length > 0) {
        try {
          await fetch('/cart/add.js', {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({
              items: toAdd.map((g) => ({
                id: parseInt(g.variantId, 10),
                quantity: 1,
                properties: { _es_regalo: g.tier },
              })),
            }),
          });
          // Notify UI so we can show a toast like "Te hemos añadido X gratis"
          for (const g of toAdd) {
            document.dispatchEvent(new CustomEvent('eb-gift:added', { detail: { tier: g.tier } }));
          }
        } catch (_e) { /* swallow */ }
      }

      const fresh = await window.EBCart.refresh('eb-gift-reconciler');
      prevGiftKeys = extractGiftKeys((fresh || window.EBCart.state).items);

      document.dispatchEvent(new CustomEvent('cart:update', {
        detail: {
          source: 'eb-gift-reconciler',
          resource: fresh || window.EBCart.state,
          data: {
            source: 'eb-gift-reconciler',
            itemCount: fresh ? fresh.item_count : state.item_count,
            sections: undefined,
          },
        },
      }));
    } catch (e) {
      console.warn('[EBGiftReconciler] reconcile error', e);
    } finally {
      lock = false;
      if (queued) {
        queued = false;
        setTimeout(() => reconcile(window.EBCart.state), 60);
      }
    }
  }

  /** Forzar reconciliación (llamado desde el tray al hacer opt-in). */
  function forceReconcile() {
    if (!window.EBCart || !window.EBCart.state) return;
    if (scheduleHandle) clearTimeout(scheduleHandle);
    reconcile(window.EBCart.state);
  }

  function getState() {
    const state = window.EBCart ? window.EBCart.state : { items: [] };
    const realSubtotal = realSubtotalEur(state.items);
    return {
      config: CONFIG,
      realSubtotal,
      optedOut: Array.from(optedOut),
      items: state.items || [],
    };
  }

  window.EBGiftReconciler = {
    init,
    reconcile: forceReconcile,
    isOptedOut,
    setOptedOut,
    getState,
  };
})();
